<template>
    <div id="article_page" v-if="item && item.id" class="article-layout">
      <!-- Article Header -->
      <div class="article-header">
        <h1 class="article-title">{{ item.title }}</h1>
        <div class="article-meta">
          <span v-if="item.main_category">{{ item.main_category.name }}</span>
          <template v-if="item.year"> | {{ item.year }}</template>
          <!-- Credit is CMS-owned and read-only here; hidden when the CMS field is empty -->
          <span v-if="articleCredit" class="credit-section"> | קרדיט: {{ articleCredit }}</span>
        </div>
      </div>

      <!-- Article Image -->
      <div v-if="item.pic_hero_parsed" class="article-image-container">
        <img :src="item.pic_hero_parsed" class="article-image" :alt="item.title" />
      </div>

      <!-- Article Content -->
      <div class="article-content">
        <div class="article-body">
          <div v-if="item.text" v-html="item.text" class="content"></div>
          <div v-else-if="item.content" v-html="item.content" class="content"></div>
          <div v-else-if="item.description_full" v-html="item.description_full" class="content"></div>
          <div v-else-if="item.description" v-html="item.description" class="content"></div>
          <div v-else class="no-content">
            <p>אין תוכן להצגה כרגע.</p>
          </div>
        </div>
      </div>

      <!-- Related Articles Section -->
      <div v-if="relatedArticles && relatedArticles.length > 0" class="related-articles">
        <div class="related-header">
          <h2>תוכן נוסף עבורך</h2>
          <p>כתבות נוספות מאותה קטגוריה</p>
        </div>
        
        <div class="articles-grid">
          <div 
            v-for="article in relatedArticles" 
            :key="article.id" 
            class="article-card"
            @click="goToArticle(article.id)"
          >
            <div class="article-image">
              <img 
                v-if="article.pic_hero_parsed" 
                :src="article.pic_hero_parsed" 
                :alt="article.title"
                loading="lazy"
              />
              <div v-else class="no-image">
                <i class="fas fa-image"></i>
              </div>
            </div>
            
            <div class="article-info">
              <h3 class="article-title">{{ article.title }}</h3>
              <div class="article-meta">
                <span v-if="article.main_category">{{ article.main_category.name }}</span>
                <span v-if="article.year"> | {{ article.year }}</span>
              </div>
              <div v-if="article.description" class="article-excerpt">
                {{ truncateText(article.description, 120) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  export default {
    // eslint-disable-next-line no-unused-vars
    data() {
      return {
        id: this.$route.params.id,
        item: {},
        relatedArticles: [],
        loading: false
      };
    },
    created() {
      this.loadArticle();
    },
    computed: {
      // Credit comes from the CMS field only — empty means "render nothing".
      articleCredit() {
        return this.item && this.item.credit ? String(this.item.credit).trim() : '';
      }
    },
    methods: {
      loadArticle() {
        this.loading = true;
        this.api_call({
          url: "items/get",
          data: { id: this.id },
          onComplete: (data) => {
            this.item = data.data.data;
            document.title = this.item.title || "TOV+ כתבה";

            // Load related articles from the same category
            if (this.item.main_category && this.item.main_category.id && this.item.main_category.id !== null) {
              this.loadRelatedArticles();
            }
            
            this.loading = false;
          },
          onError: () => {
            this.loading = false;
            // eslint-disable-next-line no-console
            console.error('Error loading article');
          }
        });
      },
      
      loadRelatedArticles() {
        this.api_call({
          url: "items/get_by_category",
          data: { 
            category_id: this.item.main_category.id,
            limit: 6,
            exclude_id: this.id // Exclude current article
          },
          onComplete: (data) => {
            const articles = data.data.data || [];
            // Only show if there are articles and they're not just the current one
            if (Array.isArray(articles)) {
              this.relatedArticles = articles.filter(article => article.id !== this.id);
            } else {
              this.relatedArticles = [];
            }
          },
          onError: () => {
            // eslint-disable-next-line no-console
            console.error('Error loading related articles');
            this.relatedArticles = [];
          }
        });
      },
      
      goToArticle(articleId) {
        this.$router.push(`/article/${articleId}`);
      },
      
      truncateText(text, maxLength) {
        if (!text) return '';
        // Remove HTML tags
        const plainText = text.replace(/<[^>]*>/g, '');
        if (plainText.length <= maxLength) return plainText;
        return plainText.substring(0, maxLength) + '...';
      }
    },
  };
  </script>
  
  <style scoped lang="scss">
  /* Article Layout */
  #article_page {
    background: #000000;
    min-height: 100vh;
    color: #ffffff;
    direction: rtl;
    text-align: right;
  }

  /* Article Header */
  .article-header {
    text-align: center;
    padding: 60px 20px 40px;
    max-width: 900px;
    margin: 0 auto;
  }

  .article-title {
    font-size: 42px;
    font-weight: 700;
    margin-bottom: 20px;
    line-height: 1.2;
    color: #ffffff;
    font-family: "Heebo", sans-serif;
  }

  .article-meta {
    font-size: 16px;
    color: #aaaaaa;
    margin-bottom: 0;
    font-weight: 400;
  }

  .credit-section {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }

  .credit-input {
    background: transparent;
    border: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.3);
    color: #aaaaaa;
    padding: 2px 4px;
    font-size: 16px;
    font-family: "Heebo", sans-serif;
    min-width: 100px;
    max-width: 200px;
    outline: none;
    transition: border-color 0.2s ease;
  }

  .credit-input:focus {
    border-bottom-color: #c12026;
  }

  .credit-input::placeholder {
    color: #666666;
  }

  /* Article Image */
  .article-image-container {
    text-align: center;
    padding: 0 20px 40px;
    max-width: 1000px;
    margin: 0 auto;
  }

  .article-image {
    width: 100%;
    max-width: 800px;
    height: auto;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    object-fit: cover;
  }

  /* Article Content */
  .article-content {
    max-width: 800px;
    margin: 0 auto;
    padding: 0 20px 60px;
  }

  .article-body {
    font-size: 20px;
    line-height: 1.8;
    color: #ffffff;
    font-family: "Heebo", sans-serif;
  }

  /* Content Styling */
  .content {
    color: #ffffff !important;
    font-size: 20px !important;
    line-height: 1.8 !important;
    text-align: right !important;
    direction: rtl !important;
  }

  .content h1,
  .content h2,
  .content h3,
  .content h4,
  .content h5,
  .content h6 {
    color: #ffffff !important;
    font-weight: 600 !important;
    margin-top: 30px !important;
    margin-bottom: 20px !important;
    line-height: 1.4 !important;
  }

  .content h1 { font-size: 32px !important; }
  .content h2 { font-size: 28px !important; }
  .content h3 { font-size: 24px !important; }
  .content h4 { font-size: 22px !important; }
  .content h5 { font-size: 20px !important; }
  .content h6 { font-size: 18px !important; }

  .content p {
    color: #ffffff !important;
    margin-bottom: 25px !important;
    font-size: 20px !important;
    line-height: 1.8 !important;
  }

  .content strong,
  .content b {
    font-weight: 700 !important;
    color: #ffffff !important;
  }

  .content em,
  .content i {
    font-style: italic !important;
    color: #ffffff !important;
  }

  .content ul,
  .content ol {
    margin: 20px 0 !important;
    padding-right: 30px !important;
  }

  .content li {
    margin-bottom: 10px !important;
    color: #ffffff !important;
  }

  .content blockquote {
    border-right: 4px solid #666 !important;
    padding-right: 25px !important;
    margin: 25px 0 !important;
    font-style: italic !important;
    color: #cccccc !important;
  }

  .content a {
    color: #66aaff !important;
    text-decoration: underline !important;
  }

  .content a:hover {
    color: #88bbff !important;
  }

  /* No Content */
  .no-content {
    text-align: center;
    color: #aaaaaa;
    font-style: italic;
    padding: 40px 20px;
  }

  /* Related Articles */
  .related-articles {
    max-width: 1200px;
    margin: 0 auto;
    padding: 60px 20px;
  }

  .related-header {
    text-align: center;
    margin-bottom: 40px;
  }

  .related-header h2 {
    font-size: 32px;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 10px;
  }

  .related-header p {
    font-size: 18px;
    color: #aaaaaa;
    margin: 0;
  }

  .articles-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: 30px;
    margin-top: 40px;
  }

  .article-card {
    background: #1a1a1a;
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s ease;
    border: 1px solid #333;
  }

  .article-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
    border-color: #555;
  }

  .article-image {
    position: relative;
    height: 200px;
    overflow: hidden;
  }

  .article-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  .article-card:hover .article-image img {
    transform: scale(1.05);
  }

  .no-image {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #333;
    color: #666;
    font-size: 24px;
  }

  .article-info {
    padding: 25px;
  }

  .article-info .article-title {
    font-size: 20px;
    font-weight: 600;
    color: #ffffff;
    margin-bottom: 15px;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .article-info .article-meta {
    font-size: 14px;
    color: #aaaaaa;
    margin-bottom: 15px;
  }

  .article-excerpt {
    font-size: 16px;
    color: #cccccc;
    line-height: 1.6;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  /* Responsive Design */
  @media only screen and (max-width: 768px) {
    .article-header {
      padding: 40px 20px 30px;
    }

    .article-title {
      font-size: 32px;
    }

    .article-meta {
      font-size: 14px;
    }

    .article-image-container {
      padding: 0 20px 30px;
    }

    .article-content {
      padding: 0 20px 40px;
    }

    .article-body {
      font-size: 18px;
    }

    .content {
      font-size: 18px !important;
    }

    .content p {
      font-size: 18px !important;
    }

    /* Related Articles Mobile */
    .related-articles {
      padding: 40px 15px;
    }

    .related-header h2 {
      font-size: 24px;
    }

    .related-header p {
      font-size: 16px;
    }

    .articles-grid {
      grid-template-columns: 1fr;
      gap: 20px;
      margin-top: 30px;
    }

    .article-card {
      margin-bottom: 20px;
    }

    .article-image {
      height: 180px;
    }

    .article-info {
      padding: 20px;
    }

    .article-info .article-title {
      font-size: 18px;
    }

    .article-excerpt {
      font-size: 14px;
    }
  }
  </style>
